import React, { FC, ReactElement } from 'react';
import Link from '@mui/material/Link';
import { Theme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { clsx } from 'clsx';
// Component
import Text from '../text';
import menuContent, { MenuInfos } from './menuContent';
import Space, { Type } from '../layout/space';
import Separator from '../layout/separator';
import AppearanceMenuItem from './appearanceMenuItem';
// Resources
import { getUser } from '../../localStorageAPI';
import { isExternalUrl } from '../../utils/redirect';
import { rps, stringsRes } from '../../resources/strings';

interface DrawerMenuResponsiveProps {
    noAdmin?: boolean;
    onClick?: (location: string) => void;
}

const DrawerMenuResponsive: FC<DrawerMenuResponsiveProps> = (props): ReactElement => {
    const { onClick, noAdmin = false } = props;

    const menuEntries: MenuInfos[] = menuContent.menuBuilder({});
    const menuAdminEntries: MenuInfos[] = menuContent.menuAdminBuilder({});
    const classes = useStyles();

    const renderMenu = (data: MenuInfos[]): JSX.Element[] => {
        return data.map((menu: MenuInfos, index: number): JSX.Element => {
            const { pathname } = window.location;
            const isSelected = pathname === menu.href || pathname.startsWith(menu.href);
            const separatorIndex = [4, 6];
            return (
                <React.Fragment key={menu.href}>
                    <Link
                        {...{
                            to: onClick ? undefined : menu.href,
                            target: !onClick && isExternalUrl(menu.href) ? '_blank' : '',
                            rel: 'noopener noreferrer',
                            component: onClick ? undefined : RouterLink,
                            onClick: onClick ? () => onClick(menu.href) : undefined,
                            color: 'inherit',
                            style: { textDecoration: 'none', display: 'flex', alignItems: 'center' },
                        }}
                        underline="hover"
                    >
                        {menu.icon}
                        <Space value={2} type={Type.horizontal} />
                        <Text.ShortMediumText16 text={menu.label} className={clsx(isSelected && classes.selected)} />
                    </Link>
                    {separatorIndex.includes(index) ? (
                        <>
                            <Space value={4} />
                            <Separator height={1} customColor="menu" />
                            <Space value={4} />
                        </>
                    ) : (
                        <Space value={4} />
                    )}
                </React.Fragment>
            );
        });
    };

    return (
        <div className={classes.drawerMenuRoot}>
            <Space value={5} />
            <Text.H25
                text={rps(stringsRes.booking.headerHelloResponsive, { name: getUser()?.firstname || '' })}
                className={classes.hello}
            />
            <Space value={4} />
            <Separator height={1} customColor="menu" />
            <Space value={4} />
            {renderMenu(menuEntries)}
            {!noAdmin && (
                <>
                    <Separator customColor="menu" />
                    <Space value={4} />
                    <Text.H25 text={stringsRes.roles.principal} />
                    <Space value={4} />
                    {renderMenu(menuAdminEntries)}
                </>
            )}
            <Separator height={1} customColor="menu" />
            <Space value={4} />
            <AppearanceMenuItem variant="row" />
            <Space value={4} />
        </div>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        drawerMenuRoot: {
            padding: '0px 30px',
            backgroundColor: theme.palette.background.default,
            '& .MuiLink-root': {
                color: `${theme.palette.primary.main}}!important`,
            },
        },
        hello: {
            fontSize: '20px!important',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        selected: {
            color: theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.secondary.light,
        },
    }),
);

export default DrawerMenuResponsive;
